/**
 * Lightweight browser device fingerprint for analytics + owner notifications.
 * Prefers navigator.userAgentData (Client Hints) when available and falls
 * back to the userAgent string. Requests NO extra permissions.
 *
 * iOS never exposes the concrete device model to the browser, so `model`
 * is deliberately set to null there (never guessed / spoofed).
 */

export interface DeviceInfo {
  device_type: "mobile" | "tablet" | "desktop";
  is_mobile: boolean;
  os: string;
  os_version: string | null;
  browser: string;
  browser_version: string | null;
  model: string | null;
  model_note: string | null;
  screen: { w: number; h: number; dpr: number } | null;
}

type UADataBrand = { brand: string; version: string };
type UAData = {
  mobile?: boolean;
  platform?: string;
  brands?: UADataBrand[];
  getHighEntropyValues?: (hints: string[]) => Promise<{
    model?: string;
    platformVersion?: string;
    platform?: string;
    mobile?: boolean;
    architecture?: string;
  }>;
};

function parseOsFromUA(ua: string): { os: string; version: string | null } {
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/.test(ua) && typeof document !== "undefined" && "ontouchend" in document)) {
    const m = ua.match(/OS (\d+[_\.]\d+(?:[_\.]\d+)?)/);
    return { os: "iOS", version: m ? m[1].replace(/_/g, ".") : null };
  }
  if (/Android/i.test(ua)) {
    const m = ua.match(/Android (\d+(?:\.\d+)*)/);
    return { os: "Android", version: m ? m[1] : null };
  }
  if (/Windows NT/.test(ua)) {
    const m = ua.match(/Windows NT (\d+(?:\.\d+)?)/);
    return { os: "Windows", version: m ? m[1] : null };
  }
  if (/Mac OS X/.test(ua)) {
    const m = ua.match(/Mac OS X (\d+[_\.]\d+(?:[_\.]\d+)?)/);
    return { os: "macOS", version: m ? m[1].replace(/_/g, ".") : null };
  }
  if (/Linux/.test(ua)) return { os: "Linux", version: null };
  return { os: "Unknown", version: null };
}

function parseBrowserFromUA(ua: string): { browser: string; version: string | null } {
  const tests: Array<[string, RegExp]> = [
    ["Edge", /Edg\/(\d+(?:\.\d+)*)/],
    ["Opera", /OPR\/(\d+(?:\.\d+)*)/],
    ["Chrome", /Chrome\/(\d+(?:\.\d+)*)/],
    ["Firefox", /Firefox\/(\d+(?:\.\d+)*)/],
    ["Safari", /Version\/(\d+(?:\.\d+)*).*Safari/],
  ];
  for (const [name, re] of tests) {
    const m = ua.match(re);
    if (m) return { browser: name, version: m[1] };
  }
  return { browser: "Unknown", version: null };
}

function inferType(ua: string, isMobile: boolean): "mobile" | "tablet" | "desktop" {
  if (/iPad/.test(ua) || (/Android/.test(ua) && !/Mobile/.test(ua))) return "tablet";
  if (isMobile || /iPhone|iPod|Mobile/i.test(ua)) return "mobile";
  return "desktop";
}

/**
 * Synchronous best-effort detection. Safe to call at any time in the browser
 * (returns a zeroed record during SSR).
 */
export function detectDeviceInfoSync(): DeviceInfo {
  if (typeof navigator === "undefined") {
    return {
      device_type: "desktop",
      is_mobile: false,
      os: "Unknown",
      os_version: null,
      browser: "Unknown",
      browser_version: null,
      model: null,
      model_note: null,
      screen: null,
    };
  }
  const ua = navigator.userAgent || "";
  const uaData = (navigator as unknown as { userAgentData?: UAData }).userAgentData;
  const isMobile = typeof uaData?.mobile === "boolean" ? uaData.mobile : /Mobile|iPhone|iPod|Android.*Mobile/i.test(ua);
  const { os, version } = parseOsFromUA(ua);
  const { browser, version: bver } = parseBrowserFromUA(ua);
  const type = inferType(ua, isMobile);
  const scr = typeof window !== "undefined" && window.screen
    ? { w: window.screen.width, h: window.screen.height, dpr: window.devicePixelRatio || 1 }
    : null;
  return {
    device_type: type,
    is_mobile: isMobile,
    os,
    os_version: version,
    browser,
    browser_version: bver,
    model: null,
    model_note: os === "iOS" ? "iPhone/iPad (model niet vrijgegeven door iOS)" : null,
    screen: scr,
  };
}

/**
 * High-fidelity detection: awaits Client Hints when available to pull the
 * Android device model + precise platform version. Falls back to the sync
 * result otherwise. Never throws.
 */
export async function detectDeviceInfo(): Promise<DeviceInfo> {
  const base = detectDeviceInfoSync();
  try {
    const uaData = (navigator as unknown as { userAgentData?: UAData }).userAgentData;
    if (uaData?.getHighEntropyValues) {
      const high = await uaData.getHighEntropyValues(["model", "platformVersion", "platform"]);
      if (high.platformVersion) base.os_version = high.platformVersion;
      if (high.platform && (!base.os || base.os === "Unknown")) base.os = high.platform;
      if (high.model && base.os !== "iOS") {
        base.model = high.model;
        base.model_note = null;
      }
    }
  } catch {
    /* ignore */
  }
  return base;
}

/**
 * Compact `device_platform` label to store on user_progress for web starts:
 * e.g. "web-mobile-ios", "web-desktop-windows".
 */
export function toDevicePlatformLabel(info: DeviceInfo): string {
  return `web-${info.device_type}-${info.os.toLowerCase().replace(/\s+/g, "")}`;
}
